import { ReactNode, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Flex, useBreakpointValue, useDisclosure } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilterList } from '@fortawesome/pro-regular-svg-icons';
import DetectableOverflow from 'react-detectable-overflow';
import { FiltersPanel, IconButtonWithTooltip } from 'shared-components';
import { Filter, FilterGroup } from 'types';
import { ApplicationState } from '@/store';
import { addExpanded, removeExpanded, saveSearchValue } from '@/store/filters/actions';
import { ActiveFilters } from './ActiveFilters';
import { getActiveFiltersFromParams } from './helpers';
import { useTranslation } from "react-i18next";

export interface IFiltersProps {
  filterGroups: FilterGroup[];
  filterOptionsLoading: boolean;
  refreshRowsCallback?: (filterKey?: string, filterValues?: (string | number)[]) => void;
}

/**
 * Returns a `saveFilter` function that reads params from window.location.search,
 * which is updated synchronously by navigate/history.pushState. This means rapid
 * successive calls (e.g. both ends of a NUMBER_RANGE filter) each see the latest
 * URL without needing batching or ref accumulators.
 */
export const useSaveFilter = (
  refreshRowsCallback?: (filterKey?: string, filterValues?: (string | number)[]) => void,
) => {
  const navigate = useNavigate();

  return (filterKey: string, filterValues: string[] | number[]): void => {
    const params = new URLSearchParams(window.location.search);
    params.delete(filterKey);
    for (const filterValue of filterValues) {
      params.append(filterKey, filterValue.toString());
    }
    params.set('page', '1');
    navigate({ search: params.toString() });

    if (refreshRowsCallback) refreshRowsCallback(filterKey, filterValues);
  };
};

export const Filters = (props: IFiltersProps): ReactNode => {
    const { t } = useTranslation('common');
  const { filterGroups, filterOptionsLoading, refreshRowsCallback } = props;

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [currentFilter, setCurrentFilter] = useState<Filter>();

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const activeFilters = getActiveFiltersFromParams(new URLSearchParams(location.search));

  const expanded = useSelector(({ filters }: ApplicationState) => filters.expanded);
  const searchValue = useSelector(({ filters }: ApplicationState) => filters.savedSearchValue);

  const saveFilter = useSaveFilter(refreshRowsCallback);

  const openFilterPanel = (filter?: Filter): void => {
    onOpen();
    setCurrentFilter(filter);
  };

  const closeFiltersPanelHandler = (): void => {
    onClose();
    setCurrentFilter(undefined);
  };

  const clearFilters = (): void => {
    const params = new URLSearchParams('');
    params.set('page', '1');
    navigate({ search: params.toString() });

    if (refreshRowsCallback) refreshRowsCallback();
  };

  const filterGroupExpanded = (filterGroupName: string, expanded: boolean): void => {
    if (expanded) {
      dispatch(addExpanded(filterGroupName));
      return;
    }
    dispatch(removeExpanded(filterGroupName));
  };

  const persistSearchValue = useCallback(
    function (searchValue: string): void {
      dispatch(saveSearchValue(searchValue));
    },
    [dispatch],
  );

  const [overflow, setOverflow] = useState(false);

  const isMobile = useBreakpointValue({ base: true, md: false });

  const filtersCount = Object.keys(activeFilters).length;
  const filtersDisplayed = filtersCount > 0 ? filtersCount : undefined;

  return (
    <Flex overflow="hidden" p="0.25rem" m="-0.25rem">
      {isMobile ? (
        <IconButtonWithTooltip
          label={t('common:filters')}
          onClick={openFilterPanel}
          buttonProps={{ size: 'md' }}
          iconProps={{ icon: faFilterList }}
          counter={filtersDisplayed}
        />
      ) : (
        <Button
          onClick={() => openFilterPanel()}
          id="filters-button"
          data-selector="filters-button"
          leftIcon={<FontAwesomeIcon className="utility-icon" icon={faFilterList} />}
          size={'sm'}
          flexShrink={0}
        >
          {t('common:filters')}
                              </Button>
      )}

      <Box
        as={DetectableOverflow}
        onChange={setOverflow}
        display={{ base: 'none', md: 'flex' }}
        overflow="hidden"
        position="relative"
        p="0.25rem"
        m="-0.25rem"
        _after={
          overflow && {
            content: "''",
            backgroundImage: 'linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, var(--ds-colors-chakra-body-bg) 50%);',
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: 8,
            pointerEvents: 'none',
          }
        }
      >
        {!filterOptionsLoading && (
          <ActiveFilters
            activeFilters={activeFilters}
            onClick={openFilterPanel}
            options={filterGroups}
            refreshRowsCallback={refreshRowsCallback}
          />
        )}
      </Box>

      <FiltersPanel
        isOpen={isOpen}
        gotoFilter={currentFilter}
        filters={{ expanded, activeFilters }}
        loading={filterOptionsLoading}
        filterGroups={filterGroups}
        closeFiltersPanel={closeFiltersPanelHandler}
        clearAllFilters={clearFilters}
        saveFilter={saveFilter}
        onSubmit={() => {}}
        filterGroupExpanded={filterGroupExpanded}
        saveSearchValue={persistSearchValue}
        initialSearchValue={searchValue}
      />
    </Flex>
  );
};
